import React, { useEffect, useState, useRef } from "react";
import Swal from "sweetalert2";
import { CircularProgress } from "@mui/material";
import axiosInstance from "../../config/axiosConfig";

const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL ||
  (typeof window !== "undefined" && window.location.hostname !== "localhost" && window.location.hostname !== "127.0.0.1"
    ? "https://api.uo.edu.pk/api/"
    : "http://127.0.0.1:8001/api/");

const resolveImgUrl = (img) => {
  if (!img) return "";
  if (typeof img === "string" && (img.startsWith("http://") || img.startsWith("https://"))) {
    return img;
  }
  if (typeof img === "string" && img.startsWith("/images/")) {
    return `http://localhost:3000${img}`;
  }
  const base = (API_BASE_URL || "http://127.0.0.1:8001/api/").replace(/\/api\/?$/, "");
  return `${base}/api/media/${img}`;
};

const SECTIONS = [
  {
    id: "stats",
    category: "Key Statistics",
    title: "Excellence Through the Numbers (Stats & Metrics)",
    description: "Manage the university key institutional statistics, section badge, heading, subheading, and the 3 metric cards.",
    headerFields: [
      {
        field: "stats_badge",
        label: "Section Badge / Tag",
        defaultVal: "University at a Glance",
      },
      {
        field: "stats_heading",
        label: "Section Heading",
        defaultVal: "Excellence Through the Numbers",
      },
      {
        field: "stats_subheading",
        label: "Section Subheading / Description",
        defaultVal: "Pioneering academic development, cutting-edge research, and community transformation",
        isTextarea: true,
      },
    ],
    items: [
      {
        isStatCard: true,
        field: "stat1_number",
        numberField: "stat1_number",
        titleField: "stat1_label",
        descField: "stat1_sublabel",
        label: "Metric 1 (Students)",
        badge: "Enrolled Students",
        colorBadge: "#2563eb",
        defaultNumber: "20,000+",
        defaultLabel: "Enrolled Students",
        defaultDesc: "Across comprehensive undergraduate & graduate programs",
      },
      {
        isStatCard: true,
        field: "stat2_number",
        numberField: "stat2_number",
        titleField: "stat2_label",
        descField: "stat2_sublabel",
        label: "Metric 2 (Departments)",
        badge: "Departments",
        colorBadge: "#059669",
        defaultNumber: "40+",
        defaultLabel: "Academic Departments",
        defaultDesc: "6 comprehensive multidisciplinary faculties",
      },
      {
        isStatCard: true,
        field: "stat3_number",
        numberField: "stat3_number",
        titleField: "stat3_label",
        descField: "stat3_sublabel",
        label: "Metric 3 (Faculty)",
        badge: "Faculty",
        colorBadge: "#7c3aed",
        defaultNumber: "350+",
        defaultLabel: "Qualified Faculty",
        defaultDesc: "Foreign-trained PhD scholars, researchers & professors",
      },
    ],
  },
  {
    id: "pathways",
    category: "Academic Pathways",
    title: "Academic Pathways (3 Cards)",
    description: "Manage photographic images and text for the 3 main academic degree program cards.",
    items: [
      {
        field: "undergrad_image",
        titleField: "undergrad_title",
        descField: "undergrad_desc",
        label: "Undergraduate Studies (BS)",
        badge: "BS Programs",
        defaultImg: "/images/programs/undergrad_photo.jpg",
      },
      {
        field: "graduate_image",
        titleField: "graduate_title",
        descField: "graduate_desc",
        label: "Graduate & PhD Programs",
        badge: "MS / MPhil / PhD",
        defaultImg: "/images/programs/graduate_photo.jpg",
      },
      {
        field: "skills_image",
        titleField: "skills_title",
        descField: "skills_desc",
        label: "Skills Development (SDC)",
        badge: "NAVTTC & SDC",
        defaultImg: "/images/programs/skills_photo.jpg",
      },
    ],
  },
  {
    id: "vc",
    category: "Vice Chancellor",
    title: "Vice Chancellor's Executive Welcome",
    description: "Manage the Vice Chancellor portrait photo, executive designation, and welcome message.",
    items: [
      {
        field: "vc_image",
        nameField: "vc_name",
        designationField: "vc_designation",
        descField: "vc_message",
        label: "Vice Chancellor's Portrait",
        badge: "Leadership Photo",
        defaultImg: "/images/vc-dr-sajjad-mubeen-1.webp",
        isVc: true,
      },
    ],
  },
  {
    id: "research",
    category: "Research & Innovation",
    title: "Research & Innovation / ORIC Showcase",
    description: "Manage the hero banner image and summary for the ORIC research showcase on the homepage.",
    items: [
      {
        field: "research_image",
        titleField: "research_title",
        descField: "research_desc",
        label: "ORIC Innovation Hub Showcase Banner",
        badge: "ORIC Showcase",
        defaultImg: "/images/research-showcase.jpg",
      },
    ],
  },
  {
    id: "campus_life",
    category: "Campus Experience",
    title: "Life at University of Okara",
    description: "Manage the featured campus life overview banner and introductory text.",
    items: [
      {
        field: "campus_life_image",
        titleField: "campus_life_title",
        descField: "campus_life_desc",
        label: "Campus Life & Amenities Banner",
        badge: "Campus Overview",
        defaultImg: "/images/campus-life.jpg",
      },
    ],
  },
  {
    id: "hero",
    category: "Hero Slider",
    title: "Hero Slider Default Fallback",
    description: "Default photographic background image displayed in the hero slider if no dynamic slides are published.",
    items: [
      {
        field: "hero_fallback_image",
        label: "Hero Slider Default Background",
        badge: "Hero Fallback",
        defaultImg: "/images/uo-hero-graduates.jpg",
      },
    ],
  },
];

export default function HomePageAdmin() {
  const [data, setData] = useState({});
  const [loading, setLoading] = useState(true);
  const [savingField, setSavingField] = useState(null);
  const [savingAll, setSavingAll] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState("all");

  // File objects pending upload
  const [fileMap, setFileMap] = useState({});
  // Local preview URLs
  const [previewMap, setPreviewMap] = useState({});

  const fileInputRefs = useRef({});

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      const res = await axiosInstance.get("home-settings");
      if (res?.data?.data) {
        setData(res.data.data);
      }
    } catch (err) {
      console.error("Error loading home settings:", err);
      Swal.fire({
        icon: "error",
        title: "Failed to load Home Settings",
        text: err?.response?.data?.message || err.message,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleFileChange = (field, e) => {
    const file = e.target.files?.[0];
    if (file) {
      setFileMap((prev) => ({ ...prev, [field]: file }));
      setPreviewMap((prev) => ({ ...prev, [field]: URL.createObjectURL(file) }));
    }
  };

  const handleTextChange = (field, value) => {
    setData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSaveItem = async (item) => {
    const { field, titleField, descField, nameField, designationField, numberField } = item;
    try {
      setSavingField(field);
      const formData = new FormData();

      if (fileMap[field]) {
        formData.append(field, fileMap[field]);
      }
      if (numberField && data[numberField] !== undefined) {
        formData.append(numberField, data[numberField]);
      }
      if (titleField && data[titleField] !== undefined) {
        formData.append(titleField, data[titleField]);
      }
      if (descField && data[descField] !== undefined) {
        formData.append(descField, data[descField]);
      }
      if (nameField && data[nameField] !== undefined) {
        formData.append(nameField, data[nameField]);
      }
      if (designationField && data[designationField] !== undefined) {
        formData.append(designationField, data[designationField]);
      }

      const res = await axiosInstance.post("home-settings/update", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      if (res?.data?.data) {
        setData(res.data.data);
        setFileMap((prev) => {
          const next = { ...prev };
          delete next[field];
          return next;
        });
      }

      Swal.fire({
        icon: "success",
        title: "Saved Successfully!",
        text: `${item.label} updated on the Home Page.`,
        timer: 2000,
        showConfirmButton: false,
      });
    } catch (err) {
      console.error("Error saving item:", err);
      Swal.fire({
        icon: "error",
        title: "Failed to save",
        text: err?.response?.data?.message || err.message,
      });
    } finally {
      setSavingField(null);
    }
  };

  const handleSaveHeader = async (sec) => {
    if (!sec.headerFields) return;
    try {
      setSavingField(sec.id + "_header");
      const formData = new FormData();

      sec.headerFields.forEach((hf) => {
        if (data[hf.field] !== undefined) {
          formData.append(hf.field, data[hf.field]);
        }
      });

      const res = await axiosInstance.post("home-settings/update", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      if (res?.data?.data) {
        setData(res.data.data);
      }

      Swal.fire({
        icon: "success",
        title: "Header Settings Saved!",
        text: `${sec.title} header updated.`,
        timer: 2000,
        showConfirmButton: false,
      });
    } catch (err) {
      console.error("Error saving header settings:", err);
      Swal.fire({
        icon: "error",
        title: "Failed to save header",
        text: err?.response?.data?.message || err.message,
      });
    } finally {
      setSavingField(null);
    }
  };

  const handleSaveAll = async () => {
    try {
      setSavingAll(true);
      const formData = new FormData();

      // Attach all staged files
      Object.keys(fileMap).forEach((field) => {
        if (fileMap[field]) {
          formData.append(field, fileMap[field]);
        }
      });

      // Attach all text fields
      const textFields = [
        "undergrad_title",
        "undergrad_desc",
        "graduate_title",
        "graduate_desc",
        "skills_title",
        "skills_desc",
        "vc_name",
        "vc_designation",
        "vc_message",
        "research_title",
        "research_desc",
        "campus_life_title",
        "campus_life_desc",
        "stats_badge",
        "stats_heading",
        "stats_subheading",
        "stat1_number",
        "stat1_label",
        "stat1_sublabel",
        "stat2_number",
        "stat2_label",
        "stat2_sublabel",
        "stat3_number",
        "stat3_label",
        "stat3_sublabel",
      ];

      textFields.forEach((tf) => {
        if (data[tf] !== undefined) {
          formData.append(tf, data[tf]);
        }
      });

      const res = await axiosInstance.post("home-settings/update", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      if (res?.data?.data) {
        setData(res.data.data);
        setFileMap({});
      }

      Swal.fire({
        icon: "success",
        title: "All Home Page Settings Saved!",
        text: "The website homepage has been updated.",
        timer: 2500,
        showConfirmButton: false,
      });
    } catch (err) {
      console.error("Error saving all settings:", err);
      Swal.fire({
        icon: "error",
        title: "Failed to save all settings",
        text: err?.response?.data?.message || err.message,
      });
    } finally {
      setSavingAll(false);
    }
  };

  const filteredSections =
    selectedCategory === "all"
      ? SECTIONS
      : SECTIONS.filter((s) => s.id === selectedCategory);

  if (loading) {
    return (
      <div style={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "60vh" }}>
        <CircularProgress />
      </div>
    );
  }

  return (
    <div style={{ padding: "24px 32px", maxWidth: "1400px", margin: "0 auto", color: "#1e293b" }}>
      {/* Header */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexWrap: "wrap",
          gap: "16px",
          marginBottom: "28px",
          paddingBottom: "20px",
          borderBottom: "1px solid #e2e8f0",
        }}
      >
        <div>
          <span
            style={{
              fontSize: "12px",
              fontWeight: 700,
              textTransform: "uppercase",
              letterSpacing: "1px",
              color: "#4a90e2",
              background: "#eff6ff",
              padding: "4px 10px",
              borderRadius: "20px",
              border: "1px solid #bfdbfe",
            }}
          >
            Website Content Management
          </span>
          <h1 style={{ fontSize: "28px", fontWeight: 800, margin: "8px 0 4px", color: "#0f172a" }}>
            Home Page Content & Settings
          </h1>
          <p style={{ margin: 0, color: "#64748b", fontSize: "14px" }}>
            Upload, update, and manage all images, statistics, headings, and degree pathways displayed on the University of Okara home page.
          </p>
        </div>

        <button
          type="button"
          onClick={handleSaveAll}
          disabled={savingAll}
          style={{
            padding: "12px 24px",
            backgroundColor: savingAll ? "#94a3b8" : "#16a34a",
            color: "#fff",
            border: "none",
            borderRadius: "8px",
            fontSize: "14px",
            fontWeight: "700",
            cursor: savingAll ? "not-allowed" : "pointer",
            boxShadow: "0 4px 12px rgba(22, 163, 74, 0.25)",
            display: "flex",
            alignItems: "center",
            gap: "8px",
            transition: "all 0.2s ease",
          }}
        >
          {savingAll ? (
            <>
              <CircularProgress size={16} style={{ color: "#fff" }} />
              Saving All Changes...
            </>
          ) : (
            "Save All Changes"
          )}
        </button>
      </div>

      {/* Category Tabs */}
      <div
        style={{
          display: "flex",
          gap: "8px",
          overflowX: "auto",
          marginBottom: "28px",
          paddingBottom: "4px",
        }}
      >
        <button
          type="button"
          onClick={() => setSelectedCategory("all")}
          style={{
            padding: "8px 16px",
            borderRadius: "8px",
            border: "none",
            fontSize: "13px",
            fontWeight: "600",
            cursor: "pointer",
            backgroundColor: selectedCategory === "all" ? "#4a90e2" : "#f1f5f9",
            color: selectedCategory === "all" ? "#fff" : "#475569",
            transition: "all 0.2s ease",
          }}
        >
          All Sections
        </button>
        {SECTIONS.map((sec) => (
          <button
            key={sec.id}
            type="button"
            onClick={() => setSelectedCategory(sec.id)}
            style={{
              padding: "8px 16px",
              borderRadius: "8px",
              border: "none",
              fontSize: "13px",
              fontWeight: "600",
              cursor: "pointer",
              backgroundColor: selectedCategory === sec.id ? "#4a90e2" : "#f1f5f9",
              color: selectedCategory === sec.id ? "#fff" : "#475569",
              transition: "all 0.2s ease",
            }}
          >
            {sec.category}
          </button>
        ))}
      </div>

      {/* Sections Grid */}
      <div style={{ display: "flex", flexDirection: "column", gap: "32px" }}>
        {filteredSections.map((sec) => (
          <div
            key={sec.id}
            style={{
              background: "#ffffff",
              borderRadius: "16px",
              padding: "24px",
              boxShadow: "0 2px 8px rgba(0, 0, 0, 0.05)",
              border: "1px solid #e2e8f0",
            }}
          >
            {/* Section Header */}
            <div style={{ marginBottom: "20px" }}>
              <h2 style={{ fontSize: "20px", fontWeight: "700", margin: "0 0 4px", color: "#0f172a" }}>
                {sec.title}
              </h2>
              <p style={{ margin: 0, color: "#64748b", fontSize: "13px" }}>
                {sec.description}
              </p>
            </div>

            {/* Section Header Inputs (e.g. for Stats badge, heading, subheading) */}
            {sec.headerFields && (
              <div
                style={{
                  background: "#f8fafc",
                  borderRadius: "12px",
                  padding: "18px 20px",
                  border: "1px solid #e2e8f0",
                  marginBottom: "24px",
                }}
              >
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "14px" }}>
                  <h3 style={{ fontSize: "14px", fontWeight: "700", color: "#334155", margin: 0 }}>
                    Section Titles & Subtitle
                  </h3>
                  <button
                    type="button"
                    onClick={() => handleSaveHeader(sec)}
                    disabled={savingField === sec.id + "_header"}
                    style={{
                      padding: "6px 14px",
                      backgroundColor: savingField === sec.id + "_header" ? "#94a3b8" : "#2563eb",
                      color: "#fff",
                      border: "none",
                      borderRadius: "6px",
                      fontSize: "12px",
                      fontWeight: "600",
                      cursor: savingField === sec.id + "_header" ? "not-allowed" : "pointer",
                      display: "flex",
                      alignItems: "center",
                      gap: "6px",
                    }}
                  >
                    {savingField === sec.id + "_header" ? "Saving..." : "Save Titles"}
                  </button>
                </div>

                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: "16px" }}>
                  {sec.headerFields.map((hf) => (
                    <div key={hf.field} style={{ gridColumn: hf.isTextarea ? "1 / -1" : "auto" }}>
                      <label style={{ display: "block", fontSize: "12px", fontWeight: "600", color: "#475569", marginBottom: "4px" }}>
                        {hf.label}
                      </label>
                      {hf.isTextarea ? (
                        <textarea
                          rows={2}
                          value={data[hf.field] !== undefined ? data[hf.field] : hf.defaultVal}
                          onChange={(e) => handleTextChange(hf.field, e.target.value)}
                          style={{
                            width: "100%",
                            padding: "8px 12px",
                            fontSize: "13px",
                            borderRadius: "6px",
                            border: "1px solid #cbd5e1",
                            boxSizing: "border-box",
                            outline: "none",
                            fontFamily: "inherit",
                          }}
                        />
                      ) : (
                        <input
                          type="text"
                          value={data[hf.field] !== undefined ? data[hf.field] : hf.defaultVal}
                          onChange={(e) => handleTextChange(hf.field, e.target.value)}
                          style={{
                            width: "100%",
                            padding: "8px 12px",
                            fontSize: "13px",
                            borderRadius: "6px",
                            border: "1px solid #cbd5e1",
                            boxSizing: "border-box",
                            outline: "none",
                          }}
                        />
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Cards Grid */}
            <div
              style={{
                display: "grid",
                gridTemplateColumns:
                  sec.items.length === 1
                    ? "1fr"
                    : "repeat(auto-fill, minmax(360px, 1fr))",
                gap: "24px",
              }}
            >
              {sec.items.map((item) => {
                const currentImgSrc =
                  previewMap[item.field] ||
                  resolveImgUrl(data[item.field]) ||
                  item.defaultImg;

                const hasStagedFile = !!fileMap[item.field];
                const isSavingThis = savingField === item.field;

                // STAT METRIC CARD
                if (item.isStatCard) {
                  const currentNumber = data[item.numberField] !== undefined ? data[item.numberField] : item.defaultNumber;
                  const currentTitle = data[item.titleField] !== undefined ? data[item.titleField] : item.defaultLabel;
                  const currentDesc = data[item.descField] !== undefined ? data[item.descField] : item.defaultDesc;

                  return (
                    <div
                      key={item.field}
                      style={{
                        background: "#f8fafc",
                        borderRadius: "12px",
                        padding: "20px",
                        border: "1px solid #e2e8f0",
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "space-between",
                      }}
                    >
                      <div>
                        {/* Badge & Label */}
                        <div
                          style={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            marginBottom: "16px",
                          }}
                        >
                          <h3 style={{ fontSize: "15px", fontWeight: "700", margin: 0, color: "#1e293b" }}>
                            {item.label}
                          </h3>
                          <span
                            style={{
                              fontSize: "11px",
                              fontWeight: "600",
                              background: "#eff6ff",
                              color: item.colorBadge || "#2563eb",
                              padding: "2px 8px",
                              borderRadius: "12px",
                              border: "1px solid #bfdbfe",
                            }}
                          >
                            {item.badge}
                          </span>
                        </div>

                        {/* Live Metric Preview Display */}
                        <div
                          style={{
                            padding: "20px",
                            background: "#ffffff",
                            borderRadius: "10px",
                            border: "1px solid #e2e8f0",
                            marginBottom: "16px",
                            textAlign: "center",
                            boxShadow: "0 2px 4px rgba(0,0,0,0.03)",
                          }}
                        >
                          <div style={{ fontSize: "36px", fontWeight: "900", color: "#0f172a", lineHeight: 1.1 }}>
                            {currentNumber || "0"}
                          </div>
                          <div style={{ fontSize: "15px", fontWeight: "700", color: "#334155", marginTop: "6px" }}>
                            {currentTitle || "Metric Label"}
                          </div>
                          <div style={{ fontSize: "12px", color: "#64748b", marginTop: "4px", lineHeight: 1.4 }}>
                            {currentDesc || "Metric sublabel summary"}
                          </div>
                        </div>

                        {/* Form Inputs for Stat Card */}
                        <div style={{ marginBottom: "12px" }}>
                          <label style={{ display: "block", fontSize: "12px", fontWeight: "600", color: "#475569", marginBottom: "4px" }}>
                            Display Number / Value (e.g. 20,000+, 40+, 350+)
                          </label>
                          <input
                            type="text"
                            value={data[item.numberField] !== undefined ? data[item.numberField] : item.defaultNumber}
                            onChange={(e) => handleTextChange(item.numberField, e.target.value)}
                            style={{
                              width: "100%",
                              padding: "8px 12px",
                              fontSize: "13px",
                              borderRadius: "6px",
                              border: "1px solid #cbd5e1",
                              boxSizing: "border-box",
                              outline: "none",
                              fontWeight: "700",
                            }}
                          />
                        </div>

                        <div style={{ marginBottom: "12px" }}>
                          <label style={{ display: "block", fontSize: "12px", fontWeight: "600", color: "#475569", marginBottom: "4px" }}>
                            Metric Label
                          </label>
                          <input
                            type="text"
                            value={data[item.titleField] !== undefined ? data[item.titleField] : item.defaultLabel}
                            onChange={(e) => handleTextChange(item.titleField, e.target.value)}
                            style={{
                              width: "100%",
                              padding: "8px 12px",
                              fontSize: "13px",
                              borderRadius: "6px",
                              border: "1px solid #cbd5e1",
                              boxSizing: "border-box",
                              outline: "none",
                            }}
                          />
                        </div>

                        <div style={{ marginBottom: "12px" }}>
                          <label style={{ display: "block", fontSize: "12px", fontWeight: "600", color: "#475569", marginBottom: "4px" }}>
                            Sublabel / Description
                          </label>
                          <textarea
                            rows={2}
                            value={data[item.descField] !== undefined ? data[item.descField] : item.defaultDesc}
                            onChange={(e) => handleTextChange(item.descField, e.target.value)}
                            style={{
                              width: "100%",
                              padding: "8px 12px",
                              fontSize: "13px",
                              borderRadius: "6px",
                              border: "1px solid #cbd5e1",
                              boxSizing: "border-box",
                              outline: "none",
                              fontFamily: "inherit",
                            }}
                          />
                        </div>
                      </div>

                      {/* Card Save Button */}
                      <div style={{ marginTop: "12px", paddingTop: "12px", borderTop: "1px solid #e2e8f0" }}>
                        <button
                          type="button"
                          onClick={() => handleSaveItem(item)}
                          disabled={isSavingThis}
                          style={{
                            width: "100%",
                            padding: "8px 16px",
                            backgroundColor: isSavingThis ? "#94a3b8" : "#2563eb",
                            color: "#fff",
                            border: "none",
                            borderRadius: "6px",
                            fontSize: "13px",
                            fontWeight: "600",
                            cursor: isSavingThis ? "not-allowed" : "pointer",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            gap: "6px",
                            transition: "all 0.2s ease",
                          }}
                        >
                          {isSavingThis ? (
                            <>
                              <CircularProgress size={14} style={{ color: "#fff" }} />
                              Updating...
                            </>
                          ) : (
                            "Update Metric Card"
                          )}
                        </button>
                      </div>
                    </div>
                  );
                }

                // IMAGE CARD (Pathways, VC, Research, Campus Life, Hero Fallback)
                return (
                  <div
                    key={item.field}
                    style={{
                      background: "#f8fafc",
                      borderRadius: "12px",
                      padding: "20px",
                      border: "1px solid #e2e8f0",
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "space-between",
                    }}
                  >
                    <div>
                      {/* Badge & Label */}
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          marginBottom: "12px",
                        }}
                      >
                        <h3 style={{ fontSize: "15px", fontWeight: "700", margin: 0, color: "#1e293b" }}>
                          {item.label}
                        </h3>
                        <span
                          style={{
                            fontSize: "11px",
                            fontWeight: "600",
                            background: "#e0e7ff",
                            color: "#4338ca",
                            padding: "2px 8px",
                            borderRadius: "12px",
                          }}
                        >
                          {item.badge}
                        </span>
                      </div>

                      {/* Image Preview Container */}
                      <div
                        style={{
                          position: "relative",
                          width: "100%",
                          height: item.isVc ? "280px" : "180px",
                          borderRadius: "10px",
                          overflow: "hidden",
                          background: "#0f172a",
                          marginBottom: "16px",
                          boxShadow: "0 2px 6px rgba(0,0,0,0.1)",
                        }}
                      >
                        <img
                          src={currentImgSrc}
                          alt={item.label}
                          style={{
                            width: "100%",
                            height: "100%",
                            objectFit: "cover",
                            objectPosition: "center",
                          }}
                          onError={(e) => {
                            if (item.defaultImg && !e.target.src.endsWith(item.defaultImg)) {
                              e.target.src = item.defaultImg;
                            }
                          }}
                        />
                        {hasStagedFile && (
                          <div
                            style={{
                              position: "absolute",
                              top: "8px",
                              right: "8px",
                              background: "#16a34a",
                              color: "#fff",
                              fontSize: "10px",
                              fontWeight: "700",
                              padding: "4px 8px",
                              borderRadius: "6px",
                              boxShadow: "0 2px 4px rgba(0,0,0,0.2)",
                            }}
                          >
                            New Image Selected (Unsaved)
                          </div>
                        )}
                      </div>

                      {/* Upload Button */}
                      <div style={{ marginBottom: "16px" }}>
                        <input
                          type="file"
                          accept="image/*"
                          ref={(el) => (fileInputRefs.current[item.field] = el)}
                          style={{ display: "none" }}
                          onChange={(e) => handleFileChange(item.field, e)}
                        />
                        <button
                          type="button"
                          onClick={() => fileInputRefs.current[item.field]?.click()}
                          style={{
                            width: "100%",
                            padding: "9px 16px",
                            background: "#ffffff",
                            border: "1px dashed #94a3b8",
                            borderRadius: "8px",
                            color: "#334155",
                            fontSize: "13px",
                            fontWeight: "600",
                            cursor: "pointer",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            gap: "6px",
                            transition: "all 0.2s ease",
                          }}
                        >
                          <svg
                            width="16"
                            height="16"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                          >
                            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                            <polyline points="17 8 12 3 7 8" />
                            <line x1="12" y1="3" x2="12" y2="15" />
                          </svg>
                          {hasStagedFile ? "Change Selected Image" : "Upload / Replace Image"}
                        </button>
                      </div>

                      {/* Form Inputs for Text (if present) */}
                      {item.titleField && (
                        <div style={{ marginBottom: "12px" }}>
                          <label style={{ display: "block", fontSize: "12px", fontWeight: "600", color: "#475569", marginBottom: "4px" }}>
                            Card Title
                          </label>
                          <input
                            type="text"
                            value={data[item.titleField] || ""}
                            onChange={(e) => handleTextChange(item.titleField, e.target.value)}
                            style={{
                              width: "100%",
                              padding: "8px 12px",
                              fontSize: "13px",
                              borderRadius: "6px",
                              border: "1px solid #cbd5e1",
                              boxSizing: "border-box",
                              outline: "none",
                            }}
                          />
                        </div>
                      )}

                      {item.nameField && (
                        <div style={{ marginBottom: "12px" }}>
                          <label style={{ display: "block", fontSize: "12px", fontWeight: "600", color: "#475569", marginBottom: "4px" }}>
                            Executive Name
                          </label>
                          <input
                            type="text"
                            value={data[item.nameField] || ""}
                            onChange={(e) => handleTextChange(item.nameField, e.target.value)}
                            style={{
                              width: "100%",
                              padding: "8px 12px",
                              fontSize: "13px",
                              borderRadius: "6px",
                              border: "1px solid #cbd5e1",
                              boxSizing: "border-box",
                              outline: "none",
                            }}
                          />
                        </div>
                      )}

                      {item.designationField && (
                        <div style={{ marginBottom: "12px" }}>
                          <label style={{ display: "block", fontSize: "12px", fontWeight: "600", color: "#475569", marginBottom: "4px" }}>
                            Designation / Title
                          </label>
                          <input
                            type="text"
                            value={data[item.designationField] || ""}
                            onChange={(e) => handleTextChange(item.designationField, e.target.value)}
                            style={{
                              width: "100%",
                              padding: "8px 12px",
                              fontSize: "13px",
                              borderRadius: "6px",
                              border: "1px solid #cbd5e1",
                              boxSizing: "border-box",
                              outline: "none",
                            }}
                          />
                        </div>
                      )}

                      {item.descField && (
                        <div style={{ marginBottom: "12px" }}>
                          <label style={{ display: "block", fontSize: "12px", fontWeight: "600", color: "#475569", marginBottom: "4px" }}>
                            Description / Message
                          </label>
                          <textarea
                            rows={item.isVc ? 4 : 2}
                            value={data[item.descField] || ""}
                            onChange={(e) => handleTextChange(item.descField, e.target.value)}
                            style={{
                              width: "100%",
                              padding: "8px 12px",
                              fontSize: "13px",
                              borderRadius: "6px",
                              border: "1px solid #cbd5e1",
                              boxSizing: "border-box",
                              outline: "none",
                              resize: "vertical",
                              fontFamily: "inherit",
                            }}
                          />
                        </div>
                      )}
                    </div>

                    {/* Card Save Button */}
                    <div style={{ marginTop: "12px", paddingTop: "12px", borderTop: "1px solid #e2e8f0" }}>
                      <button
                        type="button"
                        onClick={() => handleSaveItem(item)}
                        disabled={isSavingThis}
                        style={{
                          width: "100%",
                          padding: "8px 16px",
                          backgroundColor: isSavingThis ? "#94a3b8" : "#2563eb",
                          color: "#fff",
                          border: "none",
                          borderRadius: "6px",
                          fontSize: "13px",
                          fontWeight: "600",
                          cursor: isSavingThis ? "not-allowed" : "pointer",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          gap: "6px",
                          transition: "all 0.2s ease",
                        }}
                      >
                        {isSavingThis ? (
                          <>
                            <CircularProgress size={14} style={{ color: "#fff" }} />
                            Updating...
                          </>
                        ) : (
                          "Update This Section"
                        )}
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
